import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Send, CheckCircle, Truck, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, formatPrice } from "./shopData";

const COUNTRIES = [
  { code: "NL", label: "Nederland" },
  { code: "BE", label: "België" },
  { code: "DE", label: "Duitsland" },
  { code: "EU", label: "Overig EU" },
];

export default function CheckoutForm({ cart, onBack, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", postcode: "", city: "", country: "NL", notes: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    if (cart.total > 0) {
      axios.post(`${API}/shipping/calculate`, { country: form.country, subtotal: cart.total })
        .then((r) => setShipping(r.data))
        .catch(() => {});
    }
  }, [form.country, cart.total]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        ...form,
        items: cart.items.map((i) => ({ title: i.title, slug: i.slug, price: i.price, quantity: i.quantity, manufacturer_item: i.manufacturer_item || "", brand: i.brand || "" })),
      });
      if (response.data.checkout_url) {
        cart.clearCart();
        window.location.href = response.data.checkout_url;
        return;
      }
      setSent(true);
      cart.clearCart();
      toast.success("Bestelling verzonden!");
    } catch {
      toast.error("Bestelling kon niet worden verzonden. Probeer opnieuw.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="flex-1 flex items-center justify-center p-6" data-testid="order-success">
        <div className="text-center">
          <CheckCircle size={48} className="text-[#6AB04C] mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">Bestelling Ontvangen!</h3>
          <p className="text-[#888] text-sm mb-6">Je ontvangt een bevestigingsmail zodra de betaling is verwerkt.</p>
          <Button onClick={onClose} className="bg-[#FF3B30] hover:bg-[#E0342A] text-white rounded-lg">Terug naar Shop</Button>
        </div>
      </div>
    );
  }

  const inputCls = "bg-[#111] border-[#1E1E1E] text-white text-sm focus:border-[#FF3B30] rounded-lg";
  const grandTotal = shipping ? shipping.total : cart.total;

  return (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="checkout-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-[#888] hover:text-white text-sm mb-2 transition-colors"><ChevronLeft size={16} /> Terug naar winkelwagen</button>
      <h3 className="text-white font-bold text-sm uppercase tracking-wider">Gegevens</h3>
      <div className="space-y-3">
        <Input data-testid="checkout-name" required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className={inputCls} placeholder="Volledige naam *" />
        <Input data-testid="checkout-email" type="email" required value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={inputCls} placeholder="Email adres *" />
        <Input data-testid="checkout-phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className={inputCls} placeholder="Telefoonnummer" />
        <Input data-testid="checkout-address" required value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} className={inputCls} placeholder="Straat + huisnummer *" />
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="checkout-postcode" required value={form.postcode} onChange={(e) => setForm((p) => ({ ...p, postcode: e.target.value }))} className={inputCls} placeholder="Postcode *" />
          <Input data-testid="checkout-city" required value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} className={inputCls} placeholder="Plaats *" />
        </div>
        <div>
          <label className="text-[#888] text-xs mb-1.5 block">Land</label>
          <div className="grid grid-cols-2 gap-2" data-testid="checkout-country-select">
            {COUNTRIES.map((c) => (
              <button key={c.code} type="button" data-testid={`country-${c.code}`} onClick={() => setForm((p) => ({ ...p, country: c.code }))}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${form.country === c.code ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/50" : "bg-[#111] text-[#888] border-[#1E1E1E] hover:text-white hover:border-[#333]"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <textarea data-testid="checkout-notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="w-full bg-[#111] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-white text-sm resize-none focus:border-[#FF3B30] focus:outline-none" rows={3} placeholder="Opmerkingen bij je bestelling..." />
      </div>

      {/* Order summary with shipping */}
      <div className="bg-[#111] rounded-lg p-4 border border-[#1E1E1E] space-y-2.5" data-testid="checkout-summary">
        <div className="flex justify-between text-sm">
          <span className="text-[#888]">{cart.count} product(en)</span>
          <span className="text-white">{formatPrice(cart.total)}</span>
        </div>
        {shipping && (
          <>
            <div className="flex justify-between text-sm items-center">
              <span className="text-[#888] flex items-center gap-1.5"><Truck size={14} /> Verzendkosten ({shipping.country})</span>
              {shipping.free_shipping ? (
                <span className="text-[#27AE60] font-semibold flex items-center gap-1" data-testid="free-shipping-badge"><Gift size={13} /> Gratis</span>
              ) : (
                <span className="text-white" data-testid="shipping-cost">{formatPrice(shipping.shipping_cost)}</span>
              )}
            </div>
            {!shipping.free_shipping && (
              <p className="text-[#27AE60] text-[11px]" data-testid="free-shipping-hint">Nog {formatPrice(shipping.free_above - cart.total)} nodig voor gratis verzending</p>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-[#1E1E1E]">
              <span className="text-[#888]">{shipping.delivery_days} · {shipping.carrier}</span>
            </div>
          </>
        )}
        {/* BTW-opbouw (21%) */}
        <div className="pt-2 border-t border-[#1E1E1E] space-y-1">
          <div className="flex justify-between text-[12px]">
            <span className="text-[#888]">Subtotaal (excl. btw)</span>
            <span className="text-[#ccc]" data-testid="checkout-ex-vat">{formatPrice(grandTotal / 1.21)}</span>
          </div>
          <div className="flex justify-between text-[12px]">
            <span className="text-[#888]">BTW (21%)</span>
            <span className="text-[#ccc]" data-testid="checkout-vat-amount">{formatPrice(grandTotal - grandTotal / 1.21)}</span>
          </div>
        </div>
        <div className="flex justify-between pt-2 border-t border-[#1E1E1E]">
          <span className="text-white font-bold">Totaal (incl. btw)</span>
          <span className="text-white font-bold text-lg" data-testid="checkout-total">{formatPrice(grandTotal)}</span>
        </div>
      </div>

      <Button type="submit" data-testid="submit-order-btn" disabled={sending} className="w-full bg-[#FF3B30] hover:bg-[#E0342A] text-white py-5 font-bold uppercase tracking-wider rounded-lg">
        {sending ? "Doorsturen naar betaling..." : <span className="flex items-center justify-center gap-2"><Send size={18} /> Afrekenen</span>}
      </Button>
      <p className="text-[#555] text-[10px] text-center">Je wordt doorgestuurd naar een beveiligde betaalpagina (iDEAL, creditcard, etc.)</p>
      <p className="text-[#555] text-[10px] text-center">Prijzen zijn onder voorbehoud en kunnen afwijken door wisselende leverancierstarieven.</p>
    </form>
  );
}
